import React, { useContext, useEffect, useState } from "react";
import {
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonListHeader,
  IonRow,
  IonText,
  IonToolbar,
} from "@ionic/react";
import InfiniteScroll from "react-infinite-scroll-component";
import { withDeviceService, WithDeviceServiceProps } from "service";
import EsperiSpinner, { SpinnerSize } from "components/EsperiSpinner";
import ErrorView from "components/ErrorView";
import EmptyView, { EmptyViewType } from "components/EmptyView";
import { RouteComponentProps, withRouter } from "react-router";
import { LocalStorageKeys } from "util/Constants";
import { UserContext } from "providers/User";
import { useTranslation } from "react-i18next";
import "./Devices.css";
import ErrorViewSmall from "components/ErrorViewSmall";
import LoadMoreView from "components/LoadMoreView";
import DeviceCell from "./DeviceCell";
import { Device } from "../../../providers/Device/DeviceActionTypes";
import ActiveHomePicker from "../../../components/ActiveHomePicker";
import { UIContext } from "../../../providers/UI/UIAppState";
import { AdminUserRole } from "../../../providers/User/UserActionTypes";
import { DeviceContext } from "../../../providers/Device";

interface DevicesProps extends WithDeviceServiceProps, RouteComponentProps {
  hidden: boolean;
}

const Devices = (props: DevicesProps) => {
  const { state } = useContext(UserContext);
  const { state: uiState } = useContext(UIContext);
  const { state: deviceState } = useContext(DeviceContext);

  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const [nextPage, setNextPage] = useState<string>("0");
  const [hasMoreItems, setHasMoreItems] = useState<boolean>(false);
  const [isFetchMoreError, setFetchMoreError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.activeHome]);

  function fetchDevices() {
    setLoading(true);
    setError(false);
    props.deviceService
      .fetchDevices("0", uiState.activeHome)
      .then((response) => {
        setLoading(false);
        setNextPage(response.nextPage);
        setHasMoreItems(response.nextPage !== null);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
        setHasMoreItems(false);
      });
  }

  const fetchMore = () => {
    setFetchMoreError(false);
    setHasMoreItems(false);
    props.deviceService
      .fetchDevices(nextPage, uiState.activeHome)
      .then((response) => {
        setNextPage(response.nextPage);
        setHasMoreItems(response.nextPage !== null);
      })
      .catch(() => {
        setFetchMoreError(true);
        setHasMoreItems(false);
      });
  };

  useEffect(() => {
    const hidden =
      isLoading ||
      isError ||
      localStorage.getItem(LocalStorageKeys.SeenOnboarding) !== null ||
      state.firstName.length === 0;
  }, [state.firstName, isLoading, isError]);

  function renderFetchMoreError() {
    if (isFetchMoreError) {
      return (
        <ErrorViewSmall
          title={t("adminUsersLoadingMoreError")}
          onTryAgain={() => {
            setFetchMoreError(false);
            setHasMoreItems(true);
          }}
        />
      );
    }
  }

  const isEmpty = !isLoading && deviceState.devices.length === 0;
  return (
    <>
      <IonContent
        className="contentLightGrey contentMaxWidth"
        hidden={props.hidden}
      >
        <div id="contentId" className="feed">
          <EsperiSpinner size={SpinnerSize.Large} hidden={!isLoading} />
          <ErrorView
            hidden={!isError}
            onTryAgain={() => {
              fetchDevices();
            }}
          />
          <InfiniteScroll
            scrollableTarget="contentId"
            dataLength={deviceState.devices.length}
            next={fetchMore}
            hasMore={hasMoreItems}
            loader={""}
          >
            <IonGrid className="feedList ion-padding">
              <IonListHeader className="itemListHeader">
                <IonToolbar className="headerToolbar">
                  <IonButton
                    className="buttonAddNew"
                    slot="start"
                    onClick={() => {
                      props.history.push("/laitteet/uusi");
                    }}
                  >
                    {t("createDevice")}
                  </IonButton>
                  <ActiveHomePicker slot="end" />
                </IonToolbar>
              </IonListHeader>
              <IonRow hidden={isEmpty} style={{ background: "white" }}>
                <IonCol
                  size="6"
                  className="ion-no-padding ion-padding-top ion-padding-start ion-padding-end"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <IonText className="body1Bold black ion-padding-bottom">
                    {t("deviceName")}
                  </IonText>
                </IonCol>
                <IonCol
                  size="3"
                  className="ion-no-padding ion-padding-top ion-padding-start ion-padding-end"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <IonText className="body1Bold black ion-padding-bottom">
                    {t("deviceModel")}
                  </IonText>
                </IonCol>
                <IonCol
                  size="3"
                  className="ion-no-padding ion-padding-top ion-padding-start ion-padding-end"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <IonText className="body1Bold black ion-padding-bottom">
                    {t("deviceStatus")}
                  </IonText>
                </IonCol>
              </IonRow>
              <EmptyView hidden={!isEmpty} type={EmptyViewType.Devices} />
              {deviceState.devices.map((item: Device) => {
                return (
                  <DeviceCell
                    key={item.id}
                    device={item}
                    onClick={() => {
                      props.history.push("/laitteet/muokkaa/" + item.id);
                    }}
                  />
                );
              })}
              {renderFetchMoreError()}
            </IonGrid>
          </InfiniteScroll>
        </div>
      </IonContent>
    </>
  );
};

export default withRouter(withDeviceService(Devices));
